import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Check, ExternalLink, BookOpen, Calendar } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const partnershipFormSchema = z.object({
  fullName: z.string().trim().min(2, "Full name is required").max(100),
  workEmail: z.string().trim().email("Please enter a valid work email"),
  phoneNumber: z.string().trim().max(20).optional().or(z.literal("")),
  platformName: z.string().trim().min(2, "Platform name is required").max(100),
});

type PartnershipFormValues = z.infer<typeof partnershipFormSchema>;

interface RequestPartnershipModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const CALENDLY_URL = "https://calendly.com/adham-clearslot/30min";

const RequestPartnershipModal = ({ open, onOpenChange }: RequestPartnershipModalProps) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submittedRequestId, setSubmittedRequestId] = useState<string | null>(null);
  const { toast } = useToast();

  const form = useForm<PartnershipFormValues>({
    resolver: zodResolver(partnershipFormSchema),
    defaultValues: {
      fullName: "",
      workEmail: "",
      phoneNumber: "",
      platformName: "",
    },
  });

  const onSubmit = async (data: PartnershipFormValues) => {
    setIsLoading(true);
    try {
      const requestId = crypto.randomUUID();
      const { error } = await supabase
        .from("partnership_requests")
        .insert({
          id: requestId,
          full_name: data.fullName,
          work_email: data.workEmail,
          phone_number: data.phoneNumber || null,
          platform_name: data.platformName,
          role: "Unspecified",
          monthly_volume: "Unspecified",
        });

      if (error) throw error;

      setSubmittedRequestId(requestId);

      // Trigger application received + admin notification email (fire and forget)
      supabase.functions.invoke("send-application-received", {
        body: {
          email: data.workEmail,
          platformName: data.platformName,
          fullName: data.fullName,
          phoneNumber: data.phoneNumber || null,
        },
      }).catch(console.error);

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting partnership request:", error);
      toast({
        title: "Submission Error",
        description: "Failed to submit your request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleBookMeeting = async () => {
    // Update meeting_booked in DB
    if (submittedRequestId) {
      supabase
        .from("partnership_requests")
        .update({ meeting_booked: true })
        .eq("id", submittedRequestId)
        .then(({ error }) => {
          if (error) console.error("Failed to update meeting_booked:", error);
        });
    }
    window.open(CALENDLY_URL, "_blank", "noopener,noreferrer");
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setIsSubmitted(false);
      setSubmittedRequestId(null);
      form.reset();
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px] bg-background border-border">
        <AnimatePresence mode="wait">
          {!isSubmitted ? (
            <PartnershipForm
              form={form}
              onSubmit={onSubmit}
              isLoading={isLoading}
            />
          ) : (
            <SuccessState
              onBookMeeting={handleBookMeeting}
              onClose={handleClose}
            />
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
};

// --- Form Component ---
interface PartnershipFormProps {
  form: ReturnType<typeof useForm<PartnershipFormValues>>;
  onSubmit: (data: PartnershipFormValues) => Promise<void>;
  isLoading: boolean;
}

const PartnershipForm = ({ form, onSubmit, isLoading }: PartnershipFormProps) => (
  <motion.div
    key="form"
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    transition={{ duration: 0.2 }}
  >
    <DialogHeader className="space-y-3">
      <DialogTitle className="text-2xl font-bold">
        Join the Infrastructure Network
      </DialogTitle>
      <DialogDescription className="text-muted-foreground">
        ClearSlot is strictly for booking platforms. Please verify your details to initiate onboarding.
      </DialogDescription>
    </DialogHeader>

    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 mt-6">
        <FormField
          control={form.control}
          name="fullName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Full Name *</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-muted/50 border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="workEmail"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Work Email *</FormLabel>
              <FormControl>
                <Input type="email" placeholder="you@platform.com" className="bg-muted/50 border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="platformName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Platform Name *</FormLabel>
              <FormControl>
                <Input type="text" placeholder="Your booking platform" className="bg-muted/50 border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="phoneNumber"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Phone <span className="text-muted-foreground font-normal">(optional)</span></FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+45 12 34 56 78" className="bg-muted/50 border-border" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <Button type="submit" variant="hero" className="w-full mt-6" disabled={isLoading}>
          {isLoading ? "Submitting..." : "Submit Application"}
        </Button>
      </form>
    </Form>
  </motion.div>
);

// --- Success State ---
interface SuccessStateProps {
  onBookMeeting: () => void;
  onClose: () => void;
}

const SuccessState = ({ onBookMeeting, onClose }: SuccessStateProps) => (
  <motion.div
    key="success"
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.3, ease: "easeOut" }}
    className="text-center py-6"
  >
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
      className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-accent/20"
    >
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 300, damping: 20, delay: 0.2 }}
      >
        <Check className="h-8 w-8 text-accent" strokeWidth={3} />
      </motion.div>
    </motion.div>

    <h3 className="text-2xl font-bold mb-2">Welcome to the Network.</h3>
    <p className="text-muted-foreground mb-4">
      We review new applications within one business day. You'll receive a confirmation
      email shortly — check your spam folder if it doesn't arrive within the hour.
      While you wait, read our technical brief on how ClearSlot's neutral signal
      infrastructure works.
    </p>
    <a
      href="/neutrality-promise"
      className="inline-block mb-8 text-sm text-foreground underline-offset-4 hover:underline transition-colors"
    >
      Read the technical brief →
    </a>

    <div className="flex flex-col items-center gap-4">
      <Button variant="hero" size="lg" className="w-full" onClick={onBookMeeting}>
        <Calendar className="mr-2 h-4 w-4" />
        Book Intro Meeting
      </Button>

      <a
        href="/docs/integrate"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <BookOpen className="h-3.5 w-3.5" />
        Read Documentation
      </a>
    </div>
  </motion.div>
);

export default RequestPartnershipModal;
